import { useEffect, useState } from 'react'
import { WifiOff, Wifi, ListTodo } from 'lucide-react'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { useSyncManager } from '@/hooks/useSyncManager'
import PendentesModal from './PendentesModal'

/**
 * Banner global de status de conexão + atalho para itens pendentes.
 * - Offline: barra amarela fixa, com botão "Ver pendentes" se houver itens.
 * - Voltou online: barra verde por 3s.
 * - Sempre que houver pendentes (mesmo online), mostra strip discreta no topo.
 */
export default function OfflineBanner() {
  const isOnline = useOnlineStatus()
  const { pendentes, syncing, progresso } = useSyncManager()
  const [showRestored, setShowRestored] = useState(false)
  const [wasOffline, setWasOffline] = useState(false)
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true); setShowRestored(false); return
    }
    if (isOnline && wasOffline) {
      setShowRestored(true)
      const t = window.setTimeout(() => setShowRestored(false), 3000)
      return () => window.clearTimeout(t)
    }
  }, [isOnline, wasOffline])

  const showOfflineStrip = !isOnline
  const showRestoredStrip = isOnline && showRestored
  const showPendingStrip = isOnline && !showRestored && pendentes > 0

  if (!showOfflineStrip && !showRestoredStrip && !showPendingStrip) return null

  let bg = 'hsl(var(--amber))'
  let fg = 'hsl(var(--bg0))'
  if (showRestoredStrip) { bg = 'hsl(var(--emerald))'; fg = 'hsl(var(--bg0))' }
  if (showPendingStrip)  { bg = 'hsl(var(--amber-bg))'; fg = 'hsl(var(--amber))' }

  return (
    <>
      <div
        className="w-full flex items-center justify-center gap-2 px-3 py-1.5 text-[12px] font-bold"
        style={{ background: bg, color: fg }}
        role="status" aria-live="polite"
      >
        {showOfflineStrip && (
          <>
            <WifiOff className="w-3.5 h-3.5" />
            <span>Você está offline — exibindo dados salvos. Edição limitada.</span>
            {pendentes > 0 && (
              <button
                onClick={() => setShowModal(true)}
                className="ml-2 underline underline-offset-2 hover:opacity-80"
              >
                Ver {pendentes} pendente{pendentes > 1 ? 's' : ''}
              </button>
            )}
          </>
        )}
        {showRestoredStrip && (
          <>
            <Wifi className="w-3.5 h-3.5" />
            <span>
              {syncing && progresso
                ? `Sincronizando ${progresso.etapa}... (${progresso.atual}/${progresso.total})`
                : 'Conexão restaurada — atualizando dados...'}
            </span>
          </>
        )}
        {showPendingStrip && (
          <>
            <ListTodo className="w-3.5 h-3.5" />
            <span>
              {syncing && progresso
                ? `Sincronizando ${progresso.etapa}... (${progresso.atual}/${progresso.total})`
                : `${pendentes} item(ns) aguardando sincronização`}
            </span>
            <button
              onClick={() => setShowModal(true)}
              className="ml-2 underline underline-offset-2 hover:opacity-80"
            >
              Ver detalhes
            </button>
          </>
        )}
      </div>
      <PendentesModal open={showModal} onOpenChange={setShowModal} />
    </>
  )
}
